import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowRight,
  Check,
  Loader2,
  CheckCircle2,
  Package,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useLanguage } from "@/context/LanguageContext";
import { usePageMeta } from "@/hooks/usePageMeta";
import JsonLd from "@/components/JsonLd";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SITE = "https://reflectumpellicule.com";
const PRODUCT_IMAGES = ["/images/reflectum-20.webp", "/images/reflectum-35.webp"];
const PRODUCT_JSONLD = [
  {
    "@context": "https://schema.org", "@type": "Product",
    name: "Reflectum Silver 20 %, rouleau 60 po x 100 pi (500 pi²)",
    image: `${SITE}/images/reflectum-20.webp`,
    description:
      "Pellicule réfléchissante pour fenêtres en rouleau de 60 po x 100 pi. Effet miroir très prononcé : intimité complète de jour, jusqu'à 83 % de chaleur solaire rejetée, une protection contre les rayons UV.",
    brand: { "@type": "Brand", name: "Reflectum" },
    offers: {
      "@type": "Offer",
      price: "450",
      priceCurrency: "CAD",
      availability: "https://schema.org/InStock",
      url: `${SITE}/grossiste`,
    },
  },
  {
    "@context": "https://schema.org", "@type": "Product",
    name: "Reflectum Silver 35 %, rouleau 60 po x 100 pi (500 pi²)",
    image: `${SITE}/images/reflectum-35.webp`,
    description:
      "Pellicule réfléchissante pour fenêtres en rouleau de 60 po x 100 pi. Teinte plus légère : plus de lumière naturelle, effet miroir plus discret, une protection contre les rayons UV.",
    brand: { "@type": "Brand", name: "Reflectum" },
    offers: {
      "@type": "Offer",
      price: "450",
      priceCurrency: "CAD",
      availability: "https://schema.org/InStock",
      url: `${SITE}/grossiste`,
    },
  },
];

const initialForm = {
  name: "",
  company: "",
  company_type: "",
  neq: "",
  email: "",
  phone: "",
  city: "",
  product: "",
  rolls: "",
  message: "",
};

export default function Wholesale() {
  const { t, lang } = useLanguage();
  usePageMeta("wholesale");
  const w = t("wholesale");
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const wholesaleFaqLd = {
    "@context": "https://schema.org", "@type": "FAQPage",
    mainEntity: (w.faq || []).map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  const update = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  const scrollToForm = () => {
    document.getElementById("commander")?.scrollIntoView({ behavior: "smooth" });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.company || !form.company_type || !form.email || !form.phone || !form.product || !form.rolls) {
      toast.error(w.form.errorRequired);
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/wholesale-inquiries`, { ...form, rolls: Number(form.rolls), lang });
      setSubmitted(true);
      toast.success(w.form.successToast);
      setForm(initialForm);
    } catch (err) {
      const msg = err?.response?.data?.detail || w.form.errorToast;
      toast.error(typeof msg === "string" ? msg : w.form.errorGeneric);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="animate-fade-up">
      <JsonLd id="wholesale-product-20" data={PRODUCT_JSONLD[0]} />
      <JsonLd id="wholesale-product-35" data={PRODUCT_JSONLD[1]} />
      <JsonLd id="faq-wholesale" data={wholesaleFaqLd} />
      {/* HERO */}
      <section data-testid="wholesale-hero" className="py-20 sm:py-28 lg:py-40 bg-slate-950 text-white relative overflow-hidden">
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-blue-600/30 rounded-full blur-3xl" />
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative grid lg:grid-cols-[1.2fr,1fr] gap-12 lg:gap-16 items-center">
          <div>
            <div className="text-xs tracking-[0.3em] uppercase font-display font-semibold text-blue-400">{w.eyebrow}</div>
            <h1 data-testid="wholesale-title" className="font-heading text-[2.5rem] sm:text-5xl lg:text-6xl font-normal tracking-tight leading-[1.15] mt-4">
              {w.heroTitle}
            </h1>
            <p className="mt-8 text-lg lg:text-xl text-slate-300 max-w-2xl leading-relaxed">
              {w.heroSubtitle}
            </p>
            <Button
              onClick={scrollToForm}
              data-testid="wholesale-hero-cta"
              className="mt-10 rounded-full bg-blue-600 hover:bg-blue-700 text-white h-auto min-h-14 px-6 py-3.5 text-base font-medium shadow-lg shadow-black/10 whitespace-normal text-center leading-snug"
            >
              {w.heroCta}
              <ArrowRight className="ml-2 w-4 h-4 flex-shrink-0" />
            </Button>

            <div className="mt-14">
              <div className="text-xs tracking-[0.3em] uppercase font-display font-semibold text-blue-400 mb-5">{w.forWhomEyebrow}</div>
              <div className="flex flex-wrap gap-3">
                {w.forWhom.map((f) => (
                  <span key={f} className="px-4 py-2 rounded-full border border-white/15 bg-white/5 text-sm text-slate-200">
                    {f}
                  </span>
                ))}
              </div>
            </div>
          </div>

          <div data-testid="wholesale-hero-stats" className="rounded-3xl border border-[#C5A880]/25 bg-white/5 backdrop-blur p-8 lg:p-10">
            <div className="divide-y divide-white/10">
              {w.heroStats.map((s, i) => (
                <div key={s.l} className={`py-6 ${i === 0 ? "pt-0" : ""} ${i === w.heroStats.length - 1 ? "pb-0" : ""}`}>
                  <div className="font-heading text-3xl lg:text-4xl font-normal text-[#E2D3BE]">{s.v}</div>
                  <div className="text-sm text-slate-400 mt-1">{s.l}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* PRODUCTS */}
      <section data-testid="wholesale-products" className="py-20 sm:py-28 lg:py-40 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-xs tracking-[0.3em] uppercase font-display font-semibold text-blue-600">{w.productsEyebrow}</div>
          <h2 className="font-heading text-3xl lg:text-5xl font-normal tracking-tight leading-[1.25] text-slate-900 mt-3 max-w-3xl">
            {w.productsTitle}
          </h2>

          <div className="mt-14 grid lg:grid-cols-2 gap-8 lg:gap-12">
            {w.products.map((p, i) => (
              <div
                key={p.name}
                data-testid={`wholesale-product-${i}`}
                className="rounded-2xl overflow-hidden bg-white border border-slate-200 hover:border-blue-200 transition-colors flex flex-col"
              >
                <div className="aspect-[16/10] overflow-hidden bg-slate-100">
                  <img
                    src={PRODUCT_IMAGES[i]}
                    alt={p.name}
                    width={896}
                    height={1200}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-8 lg:p-10 flex flex-col flex-1">
                  <h3 className="font-heading text-2xl lg:text-3xl font-normal text-slate-900">{p.name}</h3>
                  <p className="mt-2 text-slate-600 leading-relaxed">{p.tagline}</p>
                  <div className="mt-5 text-sm text-slate-500">
                    <span className="font-medium text-slate-700">{w.formatLabel} :</span> {p.format}
                  </div>
                  {p.price && (
                    <div data-testid={`wholesale-price-${i}`} className="mt-3 font-heading text-2xl font-normal text-slate-900">
                      {p.price}
                    </div>
                  )}
                  <ul className="mt-6 space-y-3 flex-1">
                    {p.specs.map((s) => (
                      <li key={s} className="flex items-start gap-3">
                        <Check className="w-4 h-4 text-blue-500 mt-1 flex-shrink-0" strokeWidth={2.6} />
                        <span className="text-slate-700 leading-relaxed">{s}</span>
                      </li>
                    ))}
                  </ul>
                  <Button
                    onClick={scrollToForm}
                    data-testid={`wholesale-product-cta-${i}`}
                    className="mt-8 w-full rounded-full bg-blue-600 hover:bg-blue-700 text-white h-auto min-h-12 px-6 py-3 font-medium whitespace-normal text-center leading-snug"
                  >
                    {w.orderCta}
                    <ArrowRight className="ml-2 w-4 h-4 flex-shrink-0" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COMPARISON TABLE */}
      <section data-testid="wholesale-compare" className="py-20 sm:py-28 lg:py-40 bg-slate-50">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <h2 className="font-heading text-3xl lg:text-5xl font-normal tracking-tight leading-[1.25] text-slate-900 mb-14">
            {w.compareTitle}
          </h2>

          <div className="rounded-2xl overflow-hidden border border-slate-200 shadow-sm">
            <div className="grid grid-cols-[1.2fr,1fr,1fr]">
              <div className="bg-white p-4" />
              <div className="bg-slate-950 text-white p-4 font-heading font-medium text-center">Silver 20 %</div>
              <div className="bg-slate-200 text-slate-700 p-4 font-medium text-center">Silver 35 %</div>
            </div>
            {w.compareRows.map((r, i) => (
              <div key={r.label} data-testid={`wholesale-row-${i}`} className="grid grid-cols-[1.2fr,1fr,1fr] border-t border-slate-200">
                <div className="p-4 bg-white text-sm sm:text-base font-medium text-slate-900">{r.label}</div>
                <div className="p-4 bg-blue-50/70 text-sm sm:text-base text-slate-800 text-center">{r.a}</div>
                <div className="p-4 bg-white text-sm sm:text-base text-slate-600 text-center">{r.b}</div>
              </div>
            ))}
          </div>

          <p className="mt-5 text-sm text-slate-500 italic">{w.compareNote}</p>
          <p data-testid="wholesale-night-note" className="mt-3 text-sm text-slate-500 italic border-l-2 border-blue-400 pl-4">
            {w.nightNote}
          </p>
        </div>
      </section>

      {/* WHICH FILM */}
      <section data-testid="wholesale-choose" className="py-20 sm:py-28 lg:py-40 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-xs tracking-[0.3em] uppercase font-display font-semibold text-blue-600 mb-14">{w.chooseEyebrow}</div>
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
            {[
              { title: w.choose20Title, items: w.choose20, testId: "choose-20" },
              { title: w.choose35Title, items: w.choose35, testId: "choose-35" },
            ].map((col) => (
              <div key={col.testId} data-testid={col.testId} className="rounded-2xl bg-slate-50 border border-slate-200 p-8 lg:p-10">
                <h3 className="font-heading text-xl lg:text-2xl font-normal text-slate-900">{col.title}</h3>
                <ul className="mt-6 space-y-4">
                  {col.items.map((item) => (
                    <li key={item} className="flex items-start gap-3">
                      <Check className="w-4 h-4 text-blue-500 mt-1 flex-shrink-0" strokeWidth={2.6} />
                      <span className="text-slate-700 leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <p className="mt-10 text-center text-lg text-slate-700">{w.chooseHelp}</p>
          <div className="mt-8 text-center">
            <Button
              onClick={scrollToForm}
              data-testid="wholesale-choose-cta"
              className="rounded-full bg-blue-600 hover:bg-blue-700 text-white h-auto min-h-14 px-6 py-3.5 text-base font-medium shadow-lg shadow-black/10 whitespace-normal text-center leading-snug"
            >
              {w.orderCta}
              <ArrowRight className="ml-2 w-4 h-4 flex-shrink-0" />
            </Button>
          </div>
        </div>
      </section>

      {/* ADVANTAGES */}
      <section data-testid="wholesale-advantages" className="py-20 sm:py-28 lg:py-40 bg-slate-50">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="text-xs tracking-[0.3em] uppercase font-display font-semibold text-blue-600 mb-12">{w.advantagesEyebrow}</div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {w.advantages.map((a, i) => (
              <div key={a} data-testid={`wholesale-advantage-${i}`} className="rounded-2xl bg-white border border-slate-200 p-6 text-center">
                <Package className="w-6 h-6 text-blue-500 mx-auto mb-3" strokeWidth={1.8} />
                <div className="font-medium text-slate-900">{a}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section data-testid="wholesale-faq" className="py-20 sm:py-28 lg:py-40 bg-white">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <div className="text-xs tracking-[0.3em] uppercase font-display font-semibold text-blue-600 text-center mb-12">{w.faqEyebrow}</div>
          <Accordion type="single" collapsible className="w-full">
            {w.faq.map((f, i) => (
              <AccordionItem key={f.q} value={`w-${i}`} data-testid={`wholesale-faq-item-${i}`}>
                <AccordionTrigger data-testid={`wholesale-faq-trigger-${i}`} className="text-left font-heading text-lg font-normal text-slate-900 hover:text-blue-600">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent data-testid={`wholesale-faq-content-${i}`} className="text-slate-600 leading-relaxed text-base">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* ORDER FORM */}
      <section id="commander" data-testid="wholesale-form-section" className="py-20 sm:py-28 lg:py-40 bg-slate-950 text-white">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <h2 className="font-heading text-3xl lg:text-5xl font-normal tracking-tight leading-[1.25] mt-0">{w.formTitle}</h2>
          <p className="mt-4 text-slate-300 text-lg">{w.formSubtitle}</p>

          <div className="mt-10 p-6 sm:p-8 lg:p-10 rounded-2xl bg-white text-slate-900">
            {submitted ? (
              <div data-testid="wholesale-success" className="text-center py-12">
                <div className="w-16 h-16 rounded-full bg-blue-100 text-blue-600 grid place-items-center mx-auto">
                  <CheckCircle2 className="w-8 h-8" />
                </div>
                <h3 className="font-heading text-3xl font-normal text-slate-900 mt-6">{w.form.successTitle}</h3>
                <p className="mt-4 text-slate-600 max-w-md mx-auto">{w.form.successText}</p>
                <Button
                  onClick={() => setSubmitted(false)}
                  data-testid="wholesale-another"
                  variant="outline"
                  className="mt-8 rounded-full border-slate-300 hover:bg-slate-900 hover:text-white hover:border-slate-900"
                >
                  {w.form.successAnother}
                </Button>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="wholesale-form" className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <WField label={w.form.name} htmlFor="w-name">
                    <Input id="w-name" data-testid="w-input-name" value={form.name} onChange={(e) => update("name", e.target.value)} placeholder={w.form.namePlaceholder} className="rounded-xl bg-white h-12 border-slate-200 focus-visible:ring-2 focus-visible:ring-blue-600" required />
                  </WField>
                  <WField label={w.form.company} htmlFor="w-company">
                    <Input id="w-company" data-testid="w-input-company" value={form.company} onChange={(e) => update("company", e.target.value)} placeholder={w.form.companyPlaceholder} className="rounded-xl bg-white h-12 border-slate-200 focus-visible:ring-2 focus-visible:ring-blue-600" required />
                  </WField>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <WField label={w.form.companyType} htmlFor="w-company-type">
                    <Select value={form.company_type} onValueChange={(v) => update("company_type", v)}>
                      <SelectTrigger data-testid="w-select-company-type" className="rounded-xl bg-white h-12 border-slate-200">
                        <SelectValue placeholder={w.form.select} />
                      </SelectTrigger>
                      <SelectContent>
                        {w.form.companyTypes.map((ct) => (
                          <SelectItem key={ct} value={ct}>{ct}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </WField>
                  <WField label={w.form.neq} htmlFor="w-neq">
                    <Input id="w-neq" data-testid="w-input-neq" value={form.neq} onChange={(e) => update("neq", e.target.value)} placeholder={w.form.neqPlaceholder} className="rounded-xl bg-white h-12 border-slate-200 focus-visible:ring-2 focus-visible:ring-blue-600" />
                  </WField>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <WField label={w.form.email} htmlFor="w-email">
                    <Input id="w-email" data-testid="w-input-email" type="email" value={form.email} onChange={(e) => update("email", e.target.value)} placeholder={w.form.emailPlaceholder} className="rounded-xl bg-white h-12 border-slate-200 focus-visible:ring-2 focus-visible:ring-blue-600" required />
                  </WField>
                  <WField label={w.form.phone} htmlFor="w-phone">
                    <Input id="w-phone" data-testid="w-input-phone" value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder={w.form.phonePlaceholder} className="rounded-xl bg-white h-12 border-slate-200 focus-visible:ring-2 focus-visible:ring-blue-600" required />
                  </WField>
                </div>

                <div className="grid md:grid-cols-3 gap-6">
                  <WField label={w.form.city} htmlFor="w-city">
                    <Input id="w-city" data-testid="w-input-city" value={form.city} onChange={(e) => update("city", e.target.value)} placeholder={w.form.cityPlaceholder} className="rounded-xl bg-white h-12 border-slate-200 focus-visible:ring-2 focus-visible:ring-blue-600" />
                  </WField>
                  <WField label={w.form.product} htmlFor="w-product">
                    <Select value={form.product} onValueChange={(v) => update("product", v)}>
                      <SelectTrigger data-testid="w-select-product" className="rounded-xl bg-white h-12 border-slate-200">
                        <SelectValue placeholder={w.form.select} />
                      </SelectTrigger>
                      <SelectContent>
                        {w.form.productOptions.map((po) => (
                          <SelectItem key={po} value={po}>{po}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </WField>
                  <WField label={w.form.rolls} htmlFor="w-rolls">
                    <Input id="w-rolls" data-testid="w-input-rolls" type="number" min="1" value={form.rolls} onChange={(e) => update("rolls", e.target.value)} placeholder="1" className="rounded-xl bg-white h-12 border-slate-200 focus-visible:ring-2 focus-visible:ring-blue-600" required />
                  </WField>
                </div>

                <WField label={w.form.message} htmlFor="w-message">
                  <Textarea id="w-message" data-testid="w-input-message" value={form.message} onChange={(e) => update("message", e.target.value)} placeholder={w.form.messagePlaceholder} rows={4} className="rounded-xl bg-white border-slate-200 focus-visible:ring-2 focus-visible:ring-blue-600 resize-none" />
                </WField>

                <Button
                  type="submit"
                  data-testid="wholesale-submit"
                  disabled={loading}
                  className="w-full sm:w-auto rounded-full bg-blue-600 hover:bg-blue-700 text-white h-14 px-8 text-base font-medium shadow-md shadow-black/5"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 w-4 h-4 animate-spin" /> {w.form.sending}
                    </>
                  ) : (
                    <>
                      {w.form.submit} <ArrowRight className="ml-2 w-4 h-4" />
                    </>
                  )}
                </Button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

function WField({ label, htmlFor, children }) {
  return (
    <div className="space-y-2">
      <Label htmlFor={htmlFor} className="text-sm font-medium text-slate-800">{label}</Label>
      {children}
    </div>
  );
}
